/**
 * LabAssetViewer — full-screen preview of a single Lab asset.
 *
 * A lab asset is a single self-contained HTML document (inline
 * <style>/<script>, no companion CSS/JS), so unlike the Studio slide
 * viewer this is just a sandboxed iframe pointed at the signed S3/GCS
 * URL — no postMessage/timeline control surface.
 */

import React, { useEffect, useState } from 'react';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { labAssetsAPI, type LabAsset, type LabAssetVersion } from '@/services/api';
import { VersionSelect } from './VersionSelect';

interface LabAssetViewerProps {
  conversationId: string;
  asset: LabAsset;
  onBack: () => void;
}

export const LabAssetViewer: React.FC<LabAssetViewerProps> = ({ conversationId, asset, onBack }) => {
  const [versions, setVersions] = useState<LabAssetVersion[]>([]);
  const [selectedVersion, setSelectedVersion] = useState<number>(asset.currentVersion);

  useEffect(() => {
    setSelectedVersion(asset.currentVersion);
    labAssetsAPI
      .getVersions(conversationId, asset.slug)
      .then((r) => setVersions(r.versions))
      .catch((err) => console.error('[LabAssetViewer] Failed to load versions', err));
  }, [conversationId, asset.slug, asset.currentVersion]);

  const activeViewUrl =
    versions.find((v) => v.version === selectedVersion)?.viewUrl || asset.viewUrl;

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      <div className="flex items-center gap-3 px-4 py-3 border-b border-black/5 dark:border-white/10">
        <Button variant="ghost" size="sm" onClick={onBack} className="gap-1.5 -ml-2">
          <ArrowLeft className="w-4 h-4" />
          Lab
        </Button>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium truncate">{asset.title}</div>
          {asset.description && (
            <div className="text-xs text-gray-500 dark:text-zinc-400 truncate">{asset.description}</div>
          )}
        </div>
        {versions.length > 1 && (
          <VersionSelect
            versions={versions}
            value={selectedVersion}
            latest={asset.currentVersion}
            onChange={setSelectedVersion}
          />
        )}
      </div>
      <div className="flex-1 min-h-0 bg-white dark:bg-zinc-900 relative">
        {activeViewUrl ? (
          <iframe
            key={activeViewUrl}
            src={activeViewUrl}
            title={asset.title}
            sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
            className="w-full h-full border-0"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-400">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        )}
      </div>
    </div>
  );
};

export default LabAssetViewer;
